// Helper function to generate ordered random numbers
const generateOrderedNumbers = (min, max) => {
  let n1 = Math.floor(Math.random() * (max - min + 1)) + min;
  let n2 = Math.floor(Math.random() * (max - min + 1)) + min;
  return n2 > n1 ? [n2, n1] : [n1, n2];
};

// Advanced progress gain calculation based on educational principles
export const calculateProgressGain = (isCorrect, hintCount, timeTaken = 0, difficultyFactor = 1) => {
  if (!isCorrect) return 0;
  
  // Significantly reduced base gain for slower progression
  const baseGain = 0.5; // Changed from 2.0 to 0.5
  const maxGain = 3.0;  // Changed from 10.0 to 3.0
  
  // Make difficulty multiplier more modest to slow down progression
  const difficultyMultiplier = Math.sqrt(difficultyFactor); // Square root makes the scaling less dramatic
  
  // Increased hint penalty - each hint reduces gain substantially
  const hintPenalty = Math.pow(2.0, Math.min(hintCount, 3)) - 1; // Stronger exponential penalty
  
  // Time efficiency bonus - faster solutions get more points within reasonable bounds
  // We normalize time against expected time (faster = bonus, slower = penalty)
  let timeEfficiencyFactor = 1.0;
  if (timeTaken > 0) {
    const expectedTime = 15 * difficultyFactor; // Expected time in seconds
    timeEfficiencyFactor = 1 + (0.3 * (2 / (1 + Math.exp((timeTaken - expectedTime) / 10)) - 1));
    // Reduced factor range to approximately 0.7 to 1.3 (from 0.5 to 1.5)
  }
  
  // Calculate raw gain using all factors
  const rawGain = (baseGain * difficultyMultiplier * timeEfficiencyFactor) - hintPenalty;
  
  // Ensure gain is positive but capped
  const finalGain = Math.max(0.1, Math.min(maxGain, rawGain));
  
  // Round to one decimal place for more granular progression
  return Math.round(finalGain * 10) / 10;
};

// Generate problems based on unit number and progress with advanced scaling
export const generateProblem = (unitNumber, progress) => {
  // Default to unit 1 with 0 progress if not specified
  unitNumber = unitNumber || 1;
  progress = progress || 0;
  
  // Make difficulty levels more granular - 10 primary levels instead of 5
  // For 100% progress, always use the highest difficulty
  const primaryLevel = progress >= 100 ? 9 : Math.floor(progress / 10);
  
  // Add subtle variation within each primary level for smoother progression
  // This creates 100 effective difficulty levels instead of just 5
  const subLevel = progress % 10;
  const difficultyFactor = primaryLevel / 2 + (subLevel / 20);
  
  // Periodically introduce review problems from earlier difficulty levels
  // This implements spaced repetition for better retention
  let effectiveLevel = primaryLevel;
  if (progress > 30 && Math.random() < 0.15) {
    // 15% chance to get a review problem from a previous difficulty level
    effectiveLevel = Math.max(0, primaryLevel - 1 - Math.floor(Math.random() * 2));
  }
  
  // Occasionally introduce challenge problems for motivated students
  // These appear more frequently at higher progress levels
  const challengeThreshold = Math.min(0.05 + (progress / 200), 0.15);
  if (progress < 95 && Math.random() < challengeThreshold) {
    // Up to 15% chance for a preview of a harder problem
    effectiveLevel = Math.min(9, primaryLevel + 1 + Math.floor(Math.random() * 2));
  }
  
  // Map our 0-9 levels to 0-4 for problem generation functions
  const difficultyLevel = Math.min(4, Math.floor(effectiveLevel / 2));
  
  const problem = generateProblemByType(unitNumber, difficultyLevel, difficultyFactor);
  
  // Add the difficultyFactor to the problem object for use in progress calculation
  // Give a slight boost for harder problems
  let problemDifficultyFactor = difficultyFactor + 0.5;
  
  // Occasionally introduce "milestone" problems that give slightly more progress
  // These create a sense of achievement at regular intervals
  if ((progress % 10 === 9) && Math.random() < 0.4) {
    problemDifficultyFactor *= 1.3;
  }
  
  if (typeof problem === 'object') {
    return {
      ...problem,
      difficultyFactor: problemDifficultyFactor
    };
  } else {
    return {
      display: problem,
      eval: problem,
      difficultyFactor: problemDifficultyFactor
    };
  }
};

// Generate problem by unit type with enhanced difficulty scaling
function generateProblemByType(unitNumber, difficulty, difficultyFactor) {
  switch (unitNumber) {
    case 1: // Addition
      return generateAdditionProblem(difficulty, difficultyFactor);
    case 2: // Subtraction
      return generateSubtractionProblem(difficulty, difficultyFactor);
    case 3: // Multiplication
      return generateMultiplicationProblem(difficulty, difficultyFactor);
    case 4: // Division
      return generateDivisionProblem(difficulty, difficultyFactor);
    default:
      return generateAdditionProblem(0, difficultyFactor);
  }
}

// Addition problems scaled by difficulty with educational progression
function generateAdditionProblem(difficulty, difficultyFactor) {
  // Create different problem categories that build on fundamental skills
  const problemTypes = [
    // Type 0: Single-digit addition (basic facts)
    () => {
      const n1 = Math.floor(Math.random() * 10);
      const n2 = Math.floor(Math.random() * 10);
      return `${n1} + ${n2}`;
    },
    
    // Type 1: Teen numbers (crossing 10)
    () => {
      const n1 = Math.floor(Math.random() * 6) + 5; // 5-10
      const n2 = Math.floor(Math.random() * 6) + 5; // 5-10
      return `${n1} + ${n2}`;
    },
    
    // Type 2: Two-digit addition without carrying
    () => {
      const n1 = Math.floor(Math.random() * 9) * 10 + Math.floor(Math.random() * 9); // 10-89
      const n2 = Math.floor(Math.random() * 10); // 0-9
      return `${n1} + ${n2}`;
    },
    
    // Type 3: Two-digit addition with carrying
    () => {
      const ones1 = Math.floor(Math.random() * 6) + 5; // 5-10
      const tens1 = Math.floor(Math.random() * 9) + 1; // 1-9
      const ones2 = Math.floor(Math.random() * 6) + 5; // 5-10
      const tens2 = Math.floor(Math.random() * 9); // 0-8
      return `${tens1 * 10 + ones1} + ${tens2 * 10 + ones2}`;
    },
    
    // Type 4: Multi-digit addition with carrying and larger numbers
    () => {
      const max = Math.floor(50 + difficultyFactor * 50); // Scales up to 150-300 range
      const n1 = Math.floor(Math.random() * max) + Math.floor(max/2);
      const n2 = Math.floor(Math.random() * max) + Math.floor(max/3);
      return `${n1} + ${n2}`;
    }
  ];
  
  // Choose problem type based on difficulty, with some randomness
  let typeIndex = Math.min(difficulty, 4);
  
  // Occasionally mix in problems from adjacent difficulty levels for variety
  if (Math.random() < 0.3 && typeIndex > 0) {
    typeIndex--;
  } else if (Math.random() < 0.2 && typeIndex < 4) {
    typeIndex++;
  }
  
  return problemTypes[typeIndex]();
}

// Subtraction problems with educationally sound progression
function generateSubtractionProblem(difficulty, difficultyFactor) {
  // Create different problem categories that build on fundamental skills
  const problemTypes = [
    // Type 0: Single-digit subtraction (basic facts)
    () => {
      const n2 = Math.floor(Math.random() * 10);
      const n1 = Math.floor(Math.random() * (10 - n2)) + n2; // Ensure n1 >= n2
      return `${n1} - ${n2}`;
    },
    
    // Type 1: Subtraction within 20, crossing 10
    () => {
      const n2 = Math.floor(Math.random() * 9) + 1; // 1-9
      const n1 = 10 + Math.floor(Math.random() * 10); // 10-19
      return `${n1} - ${n2}`;
    },
    
    // Type 2: Two-digit subtraction without borrowing
    () => {
      const ones2 = Math.floor(Math.random() * 10); // 0-9
      const ones1 = Math.floor(Math.random() * (10 - ones2)) + ones2; // Ensure ones1 >= ones2
      const tens = Math.floor(Math.random() * 9) + 1; // 1-9
      return `${tens * 10 + ones1} - ${ones2}`;
    },
    
    // Type 3: Two-digit subtraction with borrowing
    () => {
      const ones2 = Math.floor(Math.random() * 9) + 1; // 1-9
      const ones1 = Math.floor(Math.random() * ones2); // 0-(ones2-1)
      const tens1 = Math.floor(Math.random() * 8) + 2; // 2-9
      const tens2 = Math.floor(Math.random() * tens1); // 0-(tens1-1)
      return `${tens1 * 10 + ones1} - ${tens2 * 10 + ones2}`;
    },
    
    // Type 4: Multi-digit subtraction with multiple borrowing steps
    () => {
      // Create numbers where borrowing will be needed
      const max = Math.floor(50 + difficultyFactor * 70); // Scales up to higher ranges
      let n1 = Math.floor(Math.random() * max) + max;
      let n2;
      
      // Ensure we need to borrow by manipulating digits
      if (difficulty > 3 && Math.random() < 0.4) {
        // Sometimes allow negative results at highest difficulty
        n2 = n1 + Math.floor(Math.random() * 50);
      } else {
        // Create a number smaller than n1, but with some digits requiring borrowing
        const n1Str = n1.toString();
        let n2Str = '';
        for (let i = 0; i < n1Str.length; i++) {
          if (i < n1Str.length - 1 && Math.random() < 0.7) {
            // Make this digit larger than n1's digit to force borrowing
            const digit = Math.floor(Math.random() * (10 - parseInt(n1Str[i]))) + parseInt(n1Str[i]);
            n2Str += digit;
          } else {
            // Make this digit smaller or equal to n1's digit
            const digit = Math.floor(Math.random() * (parseInt(n1Str[i]) + 1));
            n2Str += digit;
          }
        }
        n2 = parseInt(n2Str);
        
        // Ensure n2 is smaller than n1 overall
        if (n2 >= n1) {
          n2 = Math.floor(n1 * 0.7);
        }
      }
      
      return `${n1} - ${n2}`;
    }
  ];
  
  // Choose problem type based on difficulty, with some randomness
  let typeIndex = Math.min(difficulty, 4);
  
  // Occasionally mix in problems from adjacent difficulty levels for variety
  if (Math.random() < 0.3 && typeIndex > 0) {
    typeIndex--;
  } else if (Math.random() < 0.2 && typeIndex < 4) {
    typeIndex++;
  }
  
  return problemTypes[typeIndex]();
}

// Multiplication problems with educationally sound progression
function generateMultiplicationProblem(difficulty, difficultyFactor) {
  // Create different problem categories that build on fundamental skills
  const problemTypes = [
    // Type 0: Multiplication by 2, 5, 10 (easiest facts to learn)
    () => {
      const easyFactors = [2, 5, 10];
      const factor = easyFactors[Math.floor(Math.random() * easyFactors.length)];
      const n = Math.floor(Math.random() * 10) + 1; // 1-10
      return `${factor} * ${n}`;
    },
    
    // Type 1: Multiplication tables 1-5
    () => {
      const n1 = Math.floor(Math.random() * 5) + 1; // 1-5
      const n2 = Math.floor(Math.random() * 10) + 1; // 1-10
      return `${n1} * ${n2}`;
    },
    
    // Type 2: Multiplication tables 6-9
    () => {
      const n1 = Math.floor(Math.random() * 4) + 6; // 6-9
      const n2 = Math.floor(Math.random() * 10) + 1; // 1-10
      return `${n1} * ${n2}`;
    },
    
    // Type 3: Multiplication of two-digit by one-digit numbers
    () => {
      const n1 = Math.floor(Math.random() * 90) + 10; // 10-99
      const n2 = Math.floor(Math.random() * 9) + 1; // 1-9
      return `${n1} * ${n2}`;
    },
    
    // Type 4: Multiplication of two-digit numbers
    () => {
      // Scale max values based on difficulty factor for smoother progression
      const max1 = Math.floor(20 + difficultyFactor * 30); // Scales up with progress
      const max2 = Math.floor(10 + difficultyFactor * 10); // Scales up with progress
      
      const n1 = Math.floor(Math.random() * (max1 - 10)) + 10;
      const n2 = Math.floor(Math.random() * (max2 - 2)) + 2;
      return `${n1} * ${n2}`;
    }
  ];
  
  // Choose problem type based on difficulty, with some randomness
  let typeIndex = Math.min(difficulty, 4);
  
  // Occasionally mix in problems from adjacent difficulty levels for variety
  if (Math.random() < 0.3 && typeIndex > 0) {
    typeIndex--;
  } else if (Math.random() < 0.2 && typeIndex < 4) {
    typeIndex++;
  }
  
  return problemTypes[typeIndex]();
}

// Division problems with educationally sound progression
function generateDivisionProblem(difficulty, difficultyFactor) {
  // Create different problem categories that build on fundamental skills
  const problemTypes = [
    // Type 0: Division by 2, 5, 10 with no remainder
    () => {
      const divisors = [2, 5, 10];
      const divisor = divisors[Math.floor(Math.random() * divisors.length)];
      const quotient = Math.floor(Math.random() * 10) + 1; // 1-10
      const dividend = divisor * quotient;
      
      return {
        display: `${dividend} ÷ ${divisor}`,
        eval: `${dividend} / ${divisor}`
      };
    },
    
    // Type 1: Division facts 1-25 (with no remainder)
    () => {
      const divisor = Math.floor(Math.random() * 5) + 1; // 1-5
      const quotient = Math.floor(Math.random() * 5) + 1; // 1-5
      const dividend = divisor * quotient;
      
      return {
        display: `${dividend} ÷ ${divisor}`,
        eval: `${dividend} / ${divisor}`
      };
    },
    
    // Type 2: Division facts 1-100 (with no remainder)
    () => {
      const divisor = Math.floor(Math.random() * 9) + 1; // 1-9
      const quotient = Math.floor(Math.random() * 11) + 1; // 1-11
      const dividend = divisor * quotient;
      
      return {
        display: `${dividend} ÷ ${divisor}`,
        eval: `${dividend} / ${divisor}`
      };
    },
    
    // Type 3: Division with remainder (small numbers)
    () => {
      const divisor = Math.floor(Math.random() * 9) + 2; // 2-10
      const quotient = Math.floor(Math.random() * 10) + 1; // 1-10
      const remainder = Math.floor(Math.random() * divisor); // 0-(divisor-1)
      const dividend = divisor * quotient + remainder;
      
      // If remainder is 0, this is just a regular division problem
      if (remainder === 0) {
        return {
          display: `${dividend} ÷ ${divisor}`,
          eval: `${dividend} / ${divisor}`
        };
      }
      
      // For problems with remainder, we reformat the expected answer
      return {
        display: `${dividend} ÷ ${divisor}`,
        eval: `${quotient}` // For problems with remainder, only the quotient is correct
      };
    },
    
    // Type 4: Division with larger numbers and potential remainders
    () => {
      // Scale max values based on difficulty factor for smoother progression
      const maxDivisor = Math.floor(10 + difficultyFactor * 15);
      const maxQuotient = Math.floor(10 + difficultyFactor * 40);
      
      const divisor = Math.floor(Math.random() * (maxDivisor - 2)) + 2;
      const quotient = Math.floor(Math.random() * maxQuotient) + 1;
      
      // 70% chance to include a remainder at highest difficulty
      const includeRemainder = difficulty > 3 && Math.random() < 0.7;
      
      let dividend;
      if (includeRemainder) {
        const remainder = Math.floor(Math.random() * divisor);
        dividend = divisor * quotient + remainder;
        
        return {
          display: `${dividend} ÷ ${divisor}`,
          eval: `${quotient}` // Only the quotient is correct
        };
      } else {
        dividend = divisor * quotient;
        
        return {
          display: `${dividend} ÷ ${divisor}`,
          eval: `${dividend} / ${divisor}`
        };
      }
    }
  ];
  
  // Choose problem type based on difficulty, with some randomness
  let typeIndex = Math.min(difficulty, 4);
  
  // Occasionally mix in problems from adjacent difficulty levels for variety
  if (Math.random() < 0.3 && typeIndex > 0) {
    typeIndex--;
  } else if (Math.random() < 0.2 && typeIndex < 4) {
    typeIndex++;
  }
  
  return problemTypes[typeIndex]();
}
